/**
 * The signed-in admin session.
 *
 * Holds the current admin in a signal and exposes `login` / `logout` / `can`.
 * The access token itself lives in `core/api/client` (module memory) — this
 * context deliberately never sees it, so no component can leak it into the DOM.
 *
 * Boot sequence (see `AuthGate`): call refresh once against the httpOnly cookie.
 * If it succeeds we have an access token and fetch `/admin/me`; if not, we render
 * the login screen. That is why a reload keeps you signed in without any token
 * being readable by JavaScript.
 */
import {
  createContext,
  createSignal,
  useContext,
  type Accessor,
  type ParentProps,
} from "solid-js";

import {
  api,
  bootstrapSession,
  setAccessToken,
  setSessionHint,
  setSessionLostHandler,
} from "~/core/api/client";
import { API_URL, TENANT_ID } from "~/core/api/config";
import { ApiError } from "~/core/api/errors";
import type { AdminSessionLoginResponse, AdminUser, LoginCredentials } from "~/types/auth";
import type { Permission } from "~/core/rbac/permissions";

export interface SessionValue {
  /** The signed-in admin, or null when signed out. */
  user: Accessor<AdminUser | null>;
  /** True once the initial cookie-refresh attempt has settled. */
  ready: Accessor<boolean>;
  signedIn: Accessor<boolean>;
  /** Permission check. Owners always pass, mirroring the backend's bypass. */
  can: (permission: Permission) => boolean;
  /** True if the admin holds ANY permission in the module (drives nav visibility). */
  canAccessModule: (module: string) => boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => Promise<void>;
}

const SessionContext = createContext<SessionValue>();

export function SessionProvider(props: ParentProps) {
  const [user, setUser] = createSignal<AdminUser | null>(null);
  const [ready, setReady] = createSignal(false);

  // A refresh failure mid-session means the session is genuinely over.
  setSessionLostHandler(() => {
    setUser(null);
    setSessionHint(false);
  });

  const loadMe = async () => {
    const me = await api.get<AdminUser>("/admin/me");
    setUser(me);
  };

  /** Recover a session from the httpOnly refresh cookie, if there is one. */
  const boot = async () => {
    try {
      if (await bootstrapSession()) await loadMe();
    } catch {
      // A bad/expired cookie is a normal signed-out state, not an error to show.
      setAccessToken(null);
      setUser(null);
    } finally {
      setReady(true);
    }
  };
  void boot();

  const login = async (credentials: LoginCredentials) => {
    // Anonymous by definition, and it must send/receive cookies, so it bypasses
    // the authed client wrapper.
    const res = await fetch(`${API_URL}/admin/auth/session/login`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json", "X-Tenant-ID": TENANT_ID },
      body: JSON.stringify(credentials),
    });

    if (!res.ok) {
      let problem: Record<string, unknown> = {};
      try {
        problem = await res.json();
      } catch {
        /* non-JSON error body */
      }
      throw new ApiError(res.status, problem, res.headers.get("X-Trace-ID"));
    }

    const body = (await res.json()) as AdminSessionLoginResponse;
    setAccessToken(body.access_token);

    // The login response carries the generic auth user (id/name/email) — it has no
    // roles, is_owner or permissions. Everything RBAC depends on comes from
    // /admin/me, so fetch it before we consider the session established. Setting
    // the login payload directly would leave `permissions` undefined and every
    // `can()` check would throw.
    await loadMe();
    setSessionHint(true);
  };

  const logout = async () => {
    try {
      await api.post("/admin/auth/session/logout");
    } catch {
      // Even if the revoke call fails, drop local state — the user asked to leave.
    } finally {
      setAccessToken(null);
      setUser(null);
      setSessionHint(false);
    }
  };

  const can = (permission: Permission): boolean => {
    const u = user();
    if (!u) return false;
    if (u.is_owner) return true;
    return u.permissions.includes(permission);
  };

  const canAccessModule = (module: string): boolean => {
    const u = user();
    if (!u) return false;
    if (u.is_owner) return true;
    return u.permissions.some((p) => p.startsWith(`${module}:`));
  };

  const value: SessionValue = {
    user,
    ready,
    signedIn: () => user() !== null,
    can,
    canAccessModule,
    login,
    logout,
  };

  return <SessionContext.Provider value={value}>{props.children}</SessionContext.Provider>;
}

/** Read the session. Throws outside the provider, which keeps the type non-optional. */
export function useSession(): SessionValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error("useSession must be used within <SessionProvider>");
  return ctx;
}
